import {
  RULE_BOX_LEGENDARY_ODDS_BPS,
  RULE_BOX_MAX_LEGENDARY_ITEMS,
  RULE_BOX_NEW_MIN_RETURN_BPS,
  RULE_BOX_PRICE_MODES,
  RULE_BOX_TIER_ODDS_BPS,
  type RuleBoxPreview,
} from "@loot-os/api-contract";
import {
  dollars,
  dollarsField,
  HIGHEST_MIN_RETURN_PERCENT,
  LOWEST_MIN_RETURN_PERCENT,
  MAX_MARGIN_PERCENT,
  MIN_MARGIN_PERCENT,
  PRICE_MODE_LABEL,
  type RuleBoxForm,
  type RuleBoxFormField,
} from "@loot-os/api-contract/shopifyRuleBoxForm";
import { Figure } from "./BuilderParts";
import { fieldValue } from "./fieldValue";
import { PreviewPanel, type PreviewState } from "./PreviewPanel";

/**
 * The builder's price page: the margin the merchant keeps on average, the least any item is worth,
 * and whether the price is estimated for them or fixed at their own (which the estimate then
 * checks).
 */
export function PriceStep({
  form,
  onChange,
  errors,
  preview,
}: {
  form: RuleBoxForm;
  onChange: (patch: Partial<RuleBoxForm>) => void;
  errors: Partial<Record<RuleBoxFormField, string>>;
  preview: PreviewState;
}) {
  const margin = Number(form.marginPercent);
  const sliderValue = Number.isFinite(margin)
    ? Math.min(MAX_MARGIN_PERCENT, Math.max(MIN_MARGIN_PERCENT, margin))
    : MIN_MARGIN_PERCENT;
  const minReturnPercent = form.minReturnPercent.trim() || String(HIGHEST_MIN_RETURN_PERCENT);
  const newBoxPercent = RULE_BOX_NEW_MIN_RETURN_BPS / 100;
  return (
    <s-stack gap="base">
      <s-stack gap="small-200">
        <s-text type="strong">Your margin</s-text>
        <s-paragraph>
          The share of the box price you keep, on average, after the item a customer gets. Some
          items are worth more than the box and some less; over many boxes you keep about{" "}
          {Number.isFinite(margin) ? `${margin}%` : "this share"} of what customers pay. Every item
          is worth at least {minReturnPercent}% of the box price.
        </s-paragraph>
        <s-grid gridTemplateColumns="1fr 140px" gap="base" alignItems="center">
          <input
            type="range"
            aria-label="Margin"
            min={MIN_MARGIN_PERCENT}
            max={MAX_MARGIN_PERCENT}
            step={1}
            value={sliderValue}
            onChange={(e) => onChange({ marginPercent: e.currentTarget.value })}
            style={{ width: "100%" }}
          />
          <s-number-field
            label="Margin"
            labelAccessibilityVisibility="exclusive"
            suffix="%"
            min={MIN_MARGIN_PERCENT}
            max={MAX_MARGIN_PERCENT}
            step={0.5}
            value={form.marginPercent}
            error={errors.marginPercent}
            onInput={(e) => onChange({ marginPercent: fieldValue(e) })}
          />
        </s-grid>
        <s-text color="subdued">
          From {MIN_MARGIN_PERCENT}% to {MAX_MARGIN_PERCENT}%. A lower margin makes the box a better
          deal for customers.
        </s-text>
      </s-stack>

      <s-divider />

      <s-stack gap="small-200">
        <s-text type="strong">Least any item is worth</s-text>
        <s-paragraph>
          Every item in the box is worth at least this share of the box price. At {newBoxPercent}%
          most stores fill every rarity tier: common {tierPercent(0)}, uncommon {tierPercent(1)},
          rare {tierPercent(2)} and epic {tierPercent(3)} of the odds, plus up to{" "}
          {RULE_BOX_MAX_LEGENDARY_ITEMS} legendary items, worth 6.5× the price or more, at{" "}
          {RULE_BOX_LEGENDARY_ODDS_BPS / 100}% each. Every item of a tier is equally likely. Common
          items are worth at most 70% of the price, so at {HIGHEST_MIN_RETURN_PERCENT}% the odds
          fall item by item instead.
        </s-paragraph>
        <s-number-field
          label="Least any item is worth"
          labelAccessibilityVisibility="exclusive"
          suffix="%"
          min={LOWEST_MIN_RETURN_PERCENT}
          max={HIGHEST_MIN_RETURN_PERCENT}
          step={1}
          placeholder={String(HIGHEST_MIN_RETURN_PERCENT)}
          value={form.minReturnPercent}
          error={errors.minReturnPercent}
          onInput={(e) => onChange({ minReturnPercent: fieldValue(e) })}
        />
        <s-text color="subdued">
          From {LOWEST_MIN_RETURN_PERCENT}% to {HIGHEST_MIN_RETURN_PERCENT}%. Leave empty for{" "}
          {HIGHEST_MIN_RETURN_PERCENT}%.
        </s-text>
      </s-stack>

      <s-divider />

      <s-stack gap="small-200">
        <s-text type="strong">Price</s-text>
        <s-stack direction="inline" gap="base">
          {RULE_BOX_PRICE_MODES.map((mode) => (
            <s-button
              key={mode}
              variant={mode === form.priceMode ? "primary" : "secondary"}
              onClick={() =>
                onChange(
                  // A fixed price starts from the estimate, the price the merchant most likely wants.
                  mode === "fixed" && !form.fixedPrice.trim() && preview.data?.ok
                    ? { priceMode: mode, fixedPrice: dollarsField(preview.data.priceCents) }
                    : { priceMode: mode },
                )
              }
            >
              {PRICE_MODE_LABEL[mode]}
            </s-button>
          ))}
        </s-stack>
        {form.priceMode === "auto" ? (
          <s-paragraph>
            We pick a store-friendly price, like $75, that your matching products support at your
            margin. It stays put at each daily refresh while your products still support it, and is
            estimated again only when they no longer do.
          </s-paragraph>
        ) : (
          <>
            <s-paragraph>
              Customers always pay this price. If your matching products can't support it at your
              margin, we tell you here, and at a daily refresh the box shows sold out until they
              can.
            </s-paragraph>
            <s-text-field
              label="Price customers pay"
              prefix="$"
              placeholder="75"
              inputMode="decimal"
              value={form.fixedPrice}
              error={errors.fixedPrice}
              onInput={(e) => onChange({ fixedPrice: fieldValue(e) })}
            />
          </>
        )}
      </s-stack>

      {preview.data?.ok && !preview.updating && <MarginFigure preview={preview.data} />}
      {preview.data?.ok && !preview.updating && preview.data.oddsStyle === "power_law" && (
        <s-banner tone="info">
          Your matching products can't fill the rarity tiers at this price when every item is worth
          at least {minReturnPercent}% of it, so each item's odds fall with its value.{" "}
          {Number(minReturnPercent) > newBoxPercent
            ? `Lower the least any item is worth (${newBoxPercent}% suits most stores) to spread them over the tiers.`
            : "To spread them over the tiers, add products at more prices around the box price, or let us estimate the price."}
        </s-banner>
      )}
      <PreviewPanel state={preview} compact />
    </s-stack>
  );
}

/** A rarity tier's share of tiered odds, as "47%". */
function tierPercent(rarity: number): string {
  return `${(RULE_BOX_TIER_ODDS_BPS[rarity] ?? 0) / 100}%`;
}

/** What the merchant keeps per box at the estimate's price and payout. */
function MarginFigure({ preview }: { preview: Extract<RuleBoxPreview, { ok: true }> }) {
  const kept = preview.priceCents - preview.evCents;
  const pct = preview.priceCents > 0 ? Math.round((kept / preview.priceCents) * 1000) / 10 : 0;
  return (
    <Figure
      label="You keep per box, on average"
      value={`${dollars(kept)} (${pct}%)`}
      hint={`${dollars(preview.priceCents)} price less ${dollars(preview.evCents)} average item value`}
    />
  );
}
